import { useEffect, useRef, useState } from 'react';

import Icon from './Icon.jsx';
import { PREMIUM_FEATURES } from '../lib/tiers.js';
import { billingConfig, startCheckout } from '../lib/accountClient.js';

/** £29.99 from { amount: 2999, currency: 'GBP' } — no hardcoded currency symbols. */
function formatPrice({ amount, currency }) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: amount % 100 === 0 ? 0 : 2,
  }).format(amount / 100);
}

/**
 * What a year costs at the monthly rate, against what the annual plan costs.
 *
 * This is the one struck-through price on this page, and it is struck through
 * because it is real: £53.88 is what twelve months genuinely costs anybody who
 * pays monthly, on sale right now, on this same screen. A customer can choose
 * it. That is what makes the comparison legitimate.
 *
 * It is deliberately not a "was" price. Showing a higher former price that was
 * never actually charged is a misleading price comparison under UK consumer
 * law — the DMCC Act 2024, and the CPRs before it — and it is one of the
 * things the CMA actually enforces. If a genuine higher list price is ever
 * introduced and held for a real period, this is where that would go instead,
 * and only then.
 *
 * Returns null unless the annual plan really is cheaper, so a future pricing
 * change cannot leave a saving on screen that has stopped being true.
 */
function annualSaving(plans) {
  const annual = plans?.find((p) => p.interval === 'year');
  const monthly = plans?.find((p) => p.interval === 'month');
  if (!annual || !monthly || annual.currency !== monthly.currency) return null;

  const twelveMonths = monthly.amount * 12;
  if (twelveMonths <= annual.amount) return null;

  const saved = twelveMonths - annual.amount;
  return {
    was: formatPrice({ amount: twelveMonths, currency: monthly.currency }),
    saved: formatPrice({ amount: saved, currency: annual.currency }),
    perMonth: formatPrice({
      amount: Math.round(annual.amount / 12),
      currency: annual.currency,
    }),
  };
}

/**
 * Upgrade sheet.
 *
 * The purchase button has exactly one job: get the customer to Stripe. It does
 * not unlock anything, and it cannot — premium lives on the account row and is
 * written by the webhook. A button in the browser that could grant it would be
 * a button anyone could press from the console.
 *
 * So the flow is: no account → make one (a subscription has to belong to
 * somebody) → Stripe Checkout → webhook → `/api/auth/me` starts saying premium.
 *
 * Prices come from `/api/billing/config` rather than being written here, so the
 * page and the Stripe price it charges cannot drift apart.
 */
export default function PaywallModal({ reason, signedIn, onClose, onNeedAccount }) {
  const [plans, setPlans] = useState(null);
  const [enabled, setEnabled] = useState(null); // null = still asking
  const [plan, setPlan] = useState('annual');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [trial, setTrial] = useState({ days: 0, available: false });
  const closeRef = useRef(null);

  useEffect(() => {
    let live = true;
    billingConfig()
      .then((config) => {
        if (!live) return;
        setPlans(config.plans);
        setEnabled(config.enabled);
        setTrial({ days: config.trialDays ?? 0, available: !!config.trialAvailable });
      })
      .catch(() => live && setEnabled(false));
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Stop the page behind the sheet from scrolling on mobile.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const chosen = (plans ?? []).find((p) => p.id === plan);
  const saving = annualSaving(plans);
  const offerTrial = trial.available && trial.days > 0 && enabled !== false;
  const trialPrice = chosen
    ? `${formatPrice(chosen)} a ${chosen.interval}`
    : 'the price you pick';

  async function purchase() {
    // A subscription needs an account to attach to, and asking for the card
    // first and the email afterwards loses people. Account, then checkout.
    if (!signedIn) {
      onNeedAccount();
      return;
    }
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const { url } = await startCheckout(plan);
      // Stripe's hosted page, so no card details ever touch this origin.
      window.location.assign(url);
    } catch (error) {
      setNotice(
        error.code === 'billing_unconfigured'
          ? 'Checkout isn’t connected yet. Your account is ready for it — nothing else here will need to change.'
          : error.message,
      );
      setBusy(false);
    }
  }

  const buttonLabel = !signedIn
    ? 'Create an account to continue'
    : busy
      ? 'Taking you to checkout…'
      : 'Unlock Premium';

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="paywall-title">
      <button className="modal__scrim" onClick={onClose} aria-label="Close" tabIndex={-1} />

      <div className="modal__sheet modal__sheet--premium">
        <button ref={closeRef} type="button" className="modal__close" onClick={onClose} aria-label="Close">
          <Icon name="close" size={14} />
        </button>

        <span className="modal__glyph">
          <Icon name="radiance" size={24} />
        </span>
        <h2 id="paywall-title" className="modal__title">
          {offerTrial ? `${trial.days} days free` : 'Scripture Journey Premium'}
        </h2>
        <p className="modal__sub">
          {reason ?? 'Open the whole library and study without limits.'}
        </p>

        {/* The single most persuasive thing on this sheet, so it says exactly
            what happens and when — a trial whose terms are vague is the kind
            people cancel out of on principle. */}
        {offerTrial ? (
          <p className="paywall__trial">
            Free for {trial.days} days, then {trialPrice}. Cancel any time before
            it ends and you pay nothing.
          </p>
        ) : null}

        <ul className="features">
          {PREMIUM_FEATURES.map((feature) => (
            <li key={feature.title} className="feature">
              <span className="feature__icon">
                <Icon name={feature.icon} size={18} />
              </span>
              <div>
                <strong>{feature.title}</strong>
                <p>{feature.body}</p>
              </div>
            </li>
          ))}
        </ul>

        <div className="plans" role="radiogroup" aria-label="Choose a plan">
          {(plans ?? []).map((option) => {
            const yearly = option.interval === 'year' && saving;
            return (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={plan === option.id}
                className={`plan${plan === option.id ? ' is-selected' : ''}${
                  option.best ? ' plan--best' : ''
                }`}
                onClick={() => setPlan(option.id)}
              >
                {option.best ? <span className="plan__flag">Best value</span> : null}
                <span className="plan__label">{option.label}</span>

                {/*
                  The struck-through figure is what a year costs at the monthly
                  rate — a price genuinely on sale on this screen, not a former
                  price. It is labelled as such so it cannot be read as one.
                */}
                {yearly ? (
                  <span className="plan__was">
                    <s>{saving.was}</s>
                    <span className="plan__was-label">paid monthly</span>
                  </span>
                ) : null}

                <span className="plan__price">{formatPrice(option)}</span>
                <span className="plan__per">per {option.interval}</span>
                <span className="plan__note">
                  {yearly
                    ? `Save ${saving.saved} — that is ${saving.perMonth} a month`
                    : option.note}
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="btn btn--primary btn--block btn--lg"
          onClick={purchase}
          disabled={busy || (signedIn && !plans)}
        >
          {offerTrial && enabled !== false ? `Start ${trial.days} free days` : buttonLabel}
        </button>

        {notice ? (
          <p className="modal__notice" role="status">
            {notice}
          </p>
        ) : null}

        <p className="modal__fineprint">
          {enabled === false
            ? 'Checkout isn’t connected yet — the accounts behind it are.'
            : offerTrial
              ? `Card details are taken now and nothing is charged for ${trial.days} days. Secure checkout by Stripe, cancel any time from your account.`
              : 'Secure checkout by Stripe. Renews automatically until you cancel, which you can do any time from your account. Premium follows you to every device you sign in on.'}
        </p>

        {/*
          The terms have to be reachable *before* paying, not just findable
          afterwards. UK consumers get 14 days to change their mind about a
          subscription bought online, and a seller who does not tell them so
          before they pay extends that right to twelve months — so this link is
          the difference between a fortnight of exposure and a year of it.

          Plain <a> rather than a router push: these are real server-rendered
          pages, and they open in a new tab so nobody loses a half-finished
          checkout to read them.
        */}
        {enabled === false ? null : (
          <p className="modal__legal">
            <a href="/terms" target="_blank" rel="noreferrer">
              Terms
            </a>
            <a href="/refunds" target="_blank" rel="noreferrer">
              Cancelling &amp; refunds
            </a>
            <a href="/privacy" target="_blank" rel="noreferrer">
              Privacy
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
